/* eslint-disable indent */
/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  User,
  ArrowRight,
  Clock,
} from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useLanguage } from '@/app/context/language-context';
import blogPosts from '../../../../data/blogs';
import { authService } from '@/services/auth.service';

export default function NewsBlogSection() {
  const { t } = useLanguage();
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });
  const newsSliderRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [direction, setDirection] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const titleVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  // Trigger animation when section comes into view
  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  // Navigation functions for news slider
  // const nextNewsSlide = () => {
  //   setCurrentNewsIndex((prev) => (prev + 1) % newsArticles.length);
  // };

  // const prevNewsSlide = () => {
  //   setCurrentNewsIndex(
  //     (prev) => (prev - 1 + newsArticles.length) % newsArticles.length
  //   );
  // };

  const fetchArticles = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await authService.getArticles();
      if (response?.data?.items?.length) {
        setArticles(response.data.items);
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const sortedArticles = useMemo(() => {
    return [...articles].sort((a, b) => b.sorting - a.sorting).slice(0, 5);
  }, [articles]);

  // Navigation functions for news slider
  const nextNewsSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(1);
    const newIndex = (currentNewsIndex + 1) % sortedArticles.length;
    setCurrentNewsIndex(newIndex);

    // Update scroll position for mobile
    if (newsSliderRef.current && isMobile) {
      const cardWidth =
        newsSliderRef.current.scrollWidth / sortedArticles.length;
      newsSliderRef.current.scrollTo({
        left: newIndex * cardWidth,
        behavior: 'smooth',
      });
    }

    setTimeout(() => setIsAnimating(false), 500);
  };

  const prevNewsSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(-1);
    const newIndex =
      (currentNewsIndex - 1 + sortedArticles.length) % sortedArticles.length;
    setCurrentNewsIndex(newIndex);

    // Update scroll position for mobile
    if (newsSliderRef.current && isMobile) {
      const cardWidth =
        newsSliderRef.current.scrollWidth / sortedArticles.length;
      newsSliderRef.current.scrollTo({
        left: newIndex * cardWidth,
        behavior: 'smooth',
      });
    }

    setTimeout(() => setIsAnimating(false), 500);
  };

  // Mouse drag handlers for slider
  // Mouse drag handlers for slider (mobile only)
  const handleMouseDown = (e) => {
    if (!newsSliderRef.current || !isMobile) return;
    setIsDragging(true);
    setStartX(e.pageX - newsSliderRef.current.offsetLeft);
    setScrollLeft(newsSliderRef.current.scrollLeft);
    newsSliderRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !newsSliderRef.current || !isMobile) return;
    e.preventDefault();
    const x = e.pageX - newsSliderRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    newsSliderRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    if (!isMobile) return;
    setIsDragging(false);
    if (newsSliderRef.current) {
      newsSliderRef.current.style.cursor = 'grab';

      // Calculate which news article is most visible and set currentNewsIndex accordingly
      if (newsSliderRef.current.scrollLeft > 0) {
        const cardWidth = newsSliderRef.current.offsetWidth;
        const newIndex = Math.round(
          newsSliderRef.current.scrollLeft / cardWidth
        );
        setCurrentNewsIndex(Math.min(newIndex, sortedArticles.length - 1));
      }
    }
  };

  const handleScroll = () => {
    if (!isMobile || !newsSliderRef.current) return;
    const cardWidth = newsSliderRef.current.offsetWidth;
    const newIndex = Math.round(newsSliderRef.current.scrollLeft / cardWidth);
    if (newIndex !== currentNewsIndex) {
      setCurrentNewsIndex(Math.min(newIndex, sortedArticles.length - 1));
    }
  };

  // Function to render JSX content safely
  const renderContent = (content) => {
    if (typeof content === 'string') {
      return <div>{content}</div>;
    }
    return content;
  };

  const formatDateForPost = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });

  function formatDateForArticle(dateString) {
    const [day, month, year] = dateString.split('/');
    const date = new Date(`${year}-${month}-${day}`);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  // if (isLoading) {
  //   return (
  //     <section className='py-16 px-4 bg-white dark:bg-gray-900'>
  //       <div className='text-center text-gray-500'>Loading articles...</div>
  //     </section>
  //   );
  // }

  const currentArticle = sortedArticles[currentNewsIndex] || {};

  return (
    <motion.section
      ref={ref}
      className='py-16 px-4 bg-white dark:bg-gray-900'
      variants={sectionVariants}
      initial='hidden'
      animate={controls}
    >
      <div className='max-w-7xl mx-auto'>
        {/* Section Header */}
        <div className='text-center mb-12'>
          <motion.h2
            className='text-3xl font-bold text-gray-900 dark:text-white mb-4'
            variants={titleVariants}
          >
            {t('newsInsights')}
          </motion.h2>
          <motion.p
            className='text-gray-600 dark:text-gray-400 max-w-2xl mx-auto'
            variants={titleVariants}
          >
            {t('newsInsightsDescription')}
          </motion.p>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-12'>
          {/* News/Articles Section (Left) */}
          <div className='space-y-6'>
            <div className='flex items-center justify-between mb-4'>
              <h3 className='text-xl font-bold text-gray-900 dark:text-white'>
                {t('latestNews')}
              </h3>
              <div
                className={`flex space-x-2 ${isMobile ? 'hidden' : 'block'}`}
              >
                <motion.button
                  onClick={prevNewsSlide}
                  className='p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors'
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label='Previous news'
                >
                  <ChevronLeft className='w-4 h-4' />
                </motion.button>
                <motion.button
                  onClick={nextNewsSlide}
                  className='p-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors'
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label='Next news'
                >
                  <ChevronRight className='w-4 h-4' />
                </motion.button>
              </div>
            </div>

            <div
              className={`relative ${
                isMobile ? 'cursor-grab active:cursor-grabbing' : ''
              } overflow-hidden`}
              ref={newsSliderRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onScroll={handleScroll}
              style={{
                cursor:
                  isDragging && isMobile
                    ? 'grabbing'
                    : isMobile
                      ? 'grab'
                      : 'default',
              }}
            >
              {isMobile && !isLoading ? (
                // Mobile view - horizontal scrolling
                <div
                  className='flex overflow-x-auto snap-x snap-mandatory hide-scrollbar'
                  style={{ scrollBehavior: 'smooth' }}
                >
                  {articles
                    .slice(-5)
                    .reverse()
                    .map((article, index) => (
                      <div
                        key={article._id}
                        className='min-w-full snap-center p-4'
                      >
                        <div className='bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg'>
                          <div className='relative sm:w-1/3 h-48 sm:h-auto'>
                            <Image
                              src={article.image_url || '/placeholder.svg'}
                              alt={article.title}
                              fill
                              // priority={index === 0}
                              // loading={index === 0 ? 'eager' : 'lazy'}
                              loading='lazy' // always lazy load in list/grid
                              className='object-cover'
                              loadertype='x'
                              sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw,33vw'
                              unoptimized
                            />
                            <div className='absolute top-4 left-4 bg-primarycolor text-white px-3 py-1 rounded-full text-sm font-medium z-20'>
                              {article.category
                                .replace(/_/g, ' ')
                                .replace(/\b\w/g, (char) =>
                                  char.toUpperCase()
                                )}{' '}
                            </div>
                          </div>
                          <div className='p-4 sm:w-2/3'>
                            <h4 className='text-lg font-bold text-gray-900 dark:text-white mb-2'>
                              {article.title}
                            </h4>
                            <p className='text-gray-600 dark:text-gray-400 mb-4'>
                              {article.meta_description}
                            </p>
                            <div className='flex items-center justify-between'>
                              <div className='flex items-center text-sm text-gray-500 dark:text-gray-400'>
                                <Calendar className='w-4 h-4 mr-1 ml-1' />
                                <span className='mt-1'>
                                  {formatDateForArticle(article.date)}
                                </span>
                                {/* <span className='mx-2'>•</span>
                                <User className='w-4 h-4 mr-1' />
                                <span>{article.author}</span> */}
                              </div>
                              {/* <span className='text-sm text-gray-500 dark:text-gray-400'>
                                {article.readTime}
                              </span> */}
                            </div>
                            <Link
                              rel='preload'
                              href={`/articles/${article.slug}`}
                              aria-label={`Read more about ${article.title}`}
                              className='inline-flex items-center mt-4 text-primarycolor dark:text-secondarycolor hover:underline'
                            >
                              {t('readMore')}
                              <span className='sr-only'>
                                about {article.title}
                              </span>
                              <ArrowRight className='w-4 h-4 ml-1' />
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                // Desktop view - controlled by navigation
                <AnimatePresence initial={false} mode='wait' custom={direction}>
                  {sortedArticles.length > 0 &&
                    sortedArticles[currentNewsIndex] &&
                    !isLoading && (
                      <motion.div
                        key={currentNewsIndex}
                        variants={cardVariants}
                        className='group bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg mb-2'
                        onHoverStart={() => setHoveredCard(currentArticle.slug)}
                        onHoverEnd={() => setHoveredCard(null)}
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.5 }}
                      >
                        <Link
                          rel='preload'
                          href={`/articles/${sortedArticles[currentNewsIndex].slug}`}
                          // href={`/articles/${currentArticle.slug || ''}`}
                          className='block h-full'
                        >
                          <motion.div
                            className='bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 h-full flex flex-col relative'
                            whileHover={{
                              scale: 1.03,
                              transition: { duration: 0.3 },
                            }}
                            animate={{
                              y: hoveredCard === currentArticle.slug ? -5 : 0,
                              boxShadow:
                                hoveredCard === currentArticle.slug
                                  ? '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                                  : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                            }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className='relative h-64'>
                              <Image
                                src={
                                  sortedArticles[currentNewsIndex].image_url ||
                                  '/placeholder.svg'
                                }
                                alt={sortedArticles[currentNewsIndex].title}
                                fill
                                priority={currentNewsIndex === 0}
                                loading={
                                  currentNewsIndex === 0 ? 'eager' : 'lazy'
                                }
                                className='object-cover'
                                sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
                                unoptimized
                              />
                              <div className='absolute top-4 left-4 bg-primarycolor text-white text-xs font-medium px-2 py-1 rounded-full'>
                                {sortedArticles[currentNewsIndex].category
                                  .replace(/_/g, ' ')
                                  .replace(/\b\w/g, (char) =>
                                    char.toUpperCase()
                                  )}{' '}
                              </div>
                            </div>
                            <div className='p-6'>
                              <h4 className='text-xl font-bold text-gray-900 dark:text-white mb-2'>
                                {sortedArticles[currentNewsIndex].title}
                              </h4>
                              <p className='text-gray-600 dark:text-gray-400 mb-4'>
                                {
                                  sortedArticles[currentNewsIndex]
                                    .meta_description
                                }
                              </p>
                              <div className='flex items-center justify-between'>
                                <div className='flex items-center text-sm text-gray-500 dark:text-gray-400'>
                                  <Calendar className='w-4 h-4 mr-1 ml-1' />
                                  <span className='mt-1'>
                                    {formatDateForArticle(
                                      sortedArticles[currentNewsIndex].date
                                    )}
                                  </span>
                                  {/* <span className='mx-2'>•</span> */}
                                  {/* <User className='w-4 h-4 mr-1' /> */}
                                  {/* <span>{sortedArticles[currentNewsIndex].author}</span> */}
                                </div>
                                {/* <span className='text-sm text-gray-500 dark:text-gray-400'>
                          {sortedArticles[currentNewsIndex].readTime}
                        </span> */}
                              </div>
                            </div>

                            {/* Content */}
                            <div className='px-6 pb-6 flex flex-col flex-grow relative z-20'>
                              <div className='flex items-center justify-between mt-auto'>
                                <motion.span
                                  className='text-primarycolor dark:text-secondarycolor font-medium text-sm flex items-center gap-1'
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{
                                    opacity:
                                      hoveredCard ===
                                      sortedArticles[currentNewsIndex].slug
                                        ? 1
                                        : 0,
                                    x:
                                      hoveredCard ===
                                      sortedArticles[currentNewsIndex].slug
                                        ? 0
                                        : -10,
                                  }}
                                  transition={{ duration: 0.3 }}
                                >
                                  {t('readMore')}
                                  {/* Hidden extra context for accessibility/SEO */}
                                  <span className='sr-only'>
                                    about{' '}
                                    {sortedArticles[currentNewsIndex].title}
                                  </span>
                                  <motion.svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    width='16'
                                    height='16'
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    strokeWidth='2'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    animate={{
                                      x:
                                        hoveredCard ===
                                        sortedArticles[currentNewsIndex].slug
                                          ? 5
                                          : 0,
                                    }}
                                    transition={{ duration: 0.3 }}
                                  >
                                    <path d='M5 12h14'></path>
                                    <path d='m12 5 7 7-7 7'></path>
                                  </motion.svg>
                                </motion.span>
                              </div>
                            </div>
                          </motion.div>
                        </Link>
                      </motion.div>
                    )}
                </AnimatePresence>
              )}
            </div>

            {/* Mobile scroll indicator - Only visible on mobile */}
            <div
              className={`mb-4 text-center text-sm text-gray-500 dark:text-gray-400 ${
                isMobile ? 'block' : 'hidden'
              }`}
            >
              <span className='inline-flex items-center'>
                <ChevronLeft className='w-4 h-4 mr-1' />
                Swipe to explore
                <ChevronRight className='w-4 h-4 ml-1' />
              </span>
            </div>

            {/* Pagination Dots */}
            <div
              className={`flex justify-center mt-8 space-x-2 ${
                isMobile ? 'hidden' : 'block'
              }`}
            >
              {articles
                .slice(-5)
                .reverse()
                .map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentNewsIndex(index)}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      currentNewsIndex === index
                        ? 'bg-primarycolor'
                        : 'bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600'
                    }`}
                    aria-label={`Go to news ${index + 1}`}
                  />
                ))}
            </div>

            <div className='flex justify-center mt-4'>
              <Link
                rel='preload'
                href='/articles'
                className='inline-flex items-center px-4 py-2 text-1xl font-medium bg-primarycolor hover:bg-secondarycolor text-white hover:text-black rounded-md hover:opacity-90 transition'
              >
                {t('browseAllArticles')}
                <ArrowRight className='w-4 h-4 ml-2' />
              </Link>
            </div>
          </div>

          {/* Blog Posts Section (Right) - Improved hover effects */}
          <div className='space-y-6'>
            <div className='flex items-center justify-between mb-4'>
              <h3 className='text-xl font-bold text-gray-900 dark:text-white'>
                {t('featuredBlogPosts')}
              </h3>
              <Link
                rel='preload'
                href='/blog'
                className='text-sm text-primarycolor dark:text-secondarycolor hover:underline flex items-center'
              >
                {t('viewAll')}
                <ArrowRight className='w-4 h-4 ml-1' />
              </Link>
            </div>

            <div className='space-y-8'>
              {blogPosts
                .slice(-3)
                .reverse()
                .map((post, index) => (
                  <motion.div
                    key={post.id}
                    className='relative bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-md isolate'
                    variants={cardVariants}
                    onMouseEnter={(e) => {
                      if (window.innerWidth >= 768) {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.transition =
                          'transform 0.2s ease-in-out';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (window.innerWidth >= 768) {
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                    // whileHover={{
                    //   scale: 1.05, // Zoom-out effect
                    //   transition: { duration: 0.2 },
                    //   boxShadow:
                    //     '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    // }}
                  >
                    <div className='flex flex-col sm:flex-row'>
                      <div className='relative sm:w-1/3 h-48 sm:h-auto'>
                        <Image
                          src={post.images[0] || '/placeholder.svg'}
                          alt={post.title}
                          fill
                          className='object-cover'
                          loading='lazy'
                          // priority={index === 0}
                          quality={70} // ✅ valid
                          sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
                          unoptimized
                        />
                      </div>
                      <div className='p-4 sm:w-2/3'>
                        <div className='flex items-center text-xs text-gray-500 dark:text-gray-400 mb-2'>
                          <span className='bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full'>
                            {post.category
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, (char) =>
                                char.toUpperCase()
                              )}{' '}
                          </span>
                          <span className='mx-2'>•</span>
                          <Clock className='w-3 h-3 mr-1 ml-1' />
                          <span>{post.readTime}</span>
                        </div>
                        <h4 className='text-lg font-bold text-gray-900 dark:text-white mb-1'>
                          {post.title}
                        </h4>
                        {/* <div className='text-gray-600 dark:text-gray-400 mb-3 line-clamp-2'>
                          {renderContent(post.description)}
                        </div> */}
                        <div className='flex items-center justify-between mt-auto'>
                          <div className='flex items-center text-xs text-gray-500 dark:text-gray-400'>
                            <Calendar className='w-3 h-3 mr-1 ml-1' />
                            <span className='mt-1'>
                              {formatDateForPost(post.date)}
                            </span>
                          </div>
                          <Link
                            rel='preload'
                            href={`/blog/${post.slug}`}
                            aria-label={`Read more blog ${post.slug}`}
                            className='text-sm text-primarycolor dark:text-secondarycolor hover:underline flex items-center'
                          >
                            {t('readMore')}
                            <span className='sr-only'>about {post.title}</span>
                            <ArrowRight className='w-3 h-3 ml-1' />
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Hover effect overlay */}
                    <motion.div
                      className='absolute inset-0 bg-primarycolor/5 dark:bg-secondarycolor/5 pointer-events-none'
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.2 }}
                    />
                  </motion.div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}
